// src/components/PlanViewer.jsx
// Plan-Viewer: PDF-/Bild-Rendering mit Zoom/Pan und einer deckungsgleichen
// SVG-Overlay-Ebene für Markierungen.
// US-PLAN-002: Rendering (pdf.js/Bild), Zoom/Pan, Seitenwechsel, Overlay-Ebene.
// US-PLAN-003: Werkzeug "Punkt", Zuordnung zu einer Pendenz, Anzeige/Löschen.
// US-PLAN-004: Werkzeuge "Linie"/"Fläche" (gleiche Zuordnungs-/Speicherlogik).
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import { buildPlanFileUrl } from "../services/azurePlansService";
import {
  getMarkupsForPlanVersion,
  createMarkup,
  updateMarkup,
  deleteMarkup,
} from "../services/azurePendenzenMarkupsService";
import { getPendenzen, createPendenz } from "../services/azurePendenzenService";
import { useAuth } from "../hooks/useAuth";
import { richTextToPlainText } from "../utils/richText";
import "../styles/plans.scss";

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 6;
// Ab welchem Zoom-Vielfachen die PDF-Seite intern höher aufgelöst nachgerastert
// wird (gegen Verpixelung). Bewusst kleiner als MAX_ZOOM, da die Canvas-Fläche
// quadratisch mit dem Faktor wächst.
const MAX_RENDER_OVERSAMPLE = 3;

const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

const isPdf = (blobPath) => (blobPath || "").toLowerCase().endsWith(".pdf");

// Normalisierte Punkte (0..1) auf tatsächliche Pixelkoordinaten der aktuellen
// (unskalierten) Seitengrösse umrechnen, für SVG-Attribute.
const px = (value, size) => value * size;

const PlanViewer = ({
  plan,
  versions = [],
  project = null,
  onClose,
  compact = false,
  readOnly = false,
  initialVersionId = null,
  initialSeite = 1,
  highlightMarkupIds = [],
  // Nur Markierungen dieser Pendenz-IDs zeichnen. In der Plan-Ansicht wird hier
  // die aktuell (per Suche/Filter) sichtbare Pendenz-Menge übergeben, damit die
  // Markierungen im Plan mit der Liste daneben übereinstimmen. null = alle zeigen.
  visiblePendenzIds = null,
  focusMarkupId = null,
  selectedMarkupId: controlledSelectedId,
  onMarkupSelect,
  // Direkteinstieg aus der Pendenzenliste ("Auf Plan verorten"): neue
  // Markierungen werden ohne Zuordnungs-Auswahl direkt dieser Pendenz
  // zugeordnet (siehe Pendenzen.jsx `openPlanPicker`).
  fixedPendenzId = null,
  fixedPendenzLabel = "",
  // "Neue Planpendenz" (Kombi-Erfassung): die Pendenz existiert noch nicht,
  // daher wird die gezeichnete Geometrie nicht selbst gespeichert, sondern nur
  // an den Aufrufer gemeldet (onDraftChange). Kein Zuordnungs-Popup.
  composeMode = false,
  pendingDraft = null,
  onDraftChange,
  compactHeight = null,
  // Wird nach jeder Markierungs-/Pendenz-Änderung (anlegen, löschen, Position
  // bestätigen/verschieben) aufgerufen, damit ein einbettender Elternteil mit
  // eigenem Datenbestand (z.B. die Pendenzenliste) sich neu laden kann.
  onDataChanged,
  // Wird bei einem reinen Klick (kein Verschieben) auf eine bestehende
  // Markierung mit der zugehörigen pendenzId aufgerufen, damit ein Elternteil
  // die Pendenz direkt im Bearbeiten-Modal öffnen kann (z.B. Plan-Ansicht).
  // Standardmässig nicht gesetzt, damit bestehende Einbettungen (Pläne-Tab,
  // aufklappbare Zeile) ihr bisheriges Verhalten (nur Auswahl/Footer) behalten.
  onOpenPendenz,
  // Vorbelegter Reiter im Zuordnungs-Popup nach dem Zeichnen ("existing" |
  // "new"). Die Plan-Ansicht möchte hier direkt bei "Neue Pendenz" starten.
  defaultAssignMode = "existing",
  // Wenn gesetzt, wird eine frisch gezeichnete Markierung NICHT über das kleine
  // interne Zuordnungs-Popup verarbeitet, sondern als Entwurf an den Elternteil
  // gemeldet. Die Plan-Ansicht nutzt das, um dort das VOLLE Pendenz-Formular
  // (alle Felder) zu zeigen statt des Minimal-Popups.
  onRequestAssign = null,
  // Zähler: bei jeder Änderung lädt der Viewer seine Markierungen neu. Nötig,
  // weil in der Plan-Ansicht neue Markierungen extern (im Eltern-Modal) angelegt
  // werden – ohne diesen Anstoss bliebe die intern gehaltene Markierungsliste
  // veraltet und die neue Markierung erschiene erst nach einem Remount im Plan.
  reloadToken = 0,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // US-PLAN-006: Version ist umschaltbar (Versionshistorie/Vergleich), daher
  // eigener State statt einmalig aus initialVersionId abgeleitet.
  const [versionId, setVersionId] = useState(
    () => initialVersionId || plan?.aktiveVersionId || versions[versions.length - 1]?.id
  );
  const version = useMemo(
    () => versions.find((v) => v.id === versionId) || null,
    [versions, versionId]
  );
  const handleVersionChange = (id) => {
    setVersionId(id);
    setPage(1);
  };

  const [page, setPage] = useState(initialSeite || 1);
  const [naturalSize, setNaturalSize] = useState(null); // { width, height } in CSS px bei zoom 1
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Direkteinstieg aus einer Pendenz bzw. Kombi-Erfassung startet gleich im
  // Punkt-Werkzeug, damit sofort verortet werden kann.
  const [tool, setTool] = useState(
    fixedPendenzId || composeMode ? "point" : "select"
  ); // select | point | line | area
  const [drawingPoints, setDrawingPoints] = useState([]);
  // Messen (Distanz/Fläche): flüchtige Punkte, werden nicht als Markierung gespeichert.
  const [measurePoints, setMeasurePoints] = useState([]);
  // Aktuelle Mausposition (normiert) für die Zeichen-/Mess-Vorschau: das nächste
  // Segment folgt der Maus, solange schon ein Punkt gesetzt ist ("Gummiband").
  const [hoverPoint, setHoverPoint] = useState(null);
  const [markups, setMarkups] = useState([]);
  const [assignDraft, setAssignDraft] = useState(null); // { typ, geometrie }
  const [pendenzenList, setPendenzenList] = useState([]);
  const [uncontrolledSelectedId, setUncontrolledSelectedId] = useState(null);
  const selectedMarkupId =
    controlledSelectedId !== undefined && controlledSelectedId !== null
      ? controlledSelectedId
      : uncontrolledSelectedId;
  const setSelectedMarkupId = onMarkupSelect || setUncontrolledSelectedId;

  // US-PLAN-006: Vorher-Nachher-Vergleich (Überblendung zweier Versionen)
  const [compareVersionId, setCompareVersionId] = useState("");
  const [blendOpacity, setBlendOpacity] = useState(50);
  const compareCanvasRef = useRef(null);
  const [compareImgSrc, setCompareImgSrc] = useState(null);
  const compareVersion = versions.find((v) => v.id === compareVersionId) || null;

  // US-PLAN-006: Punkt-Markierung per Ziehen verschieben (Position bestätigen/korrigieren)
  const dragRef = useRef(null); // { id, pendenzId, original, moved }
  const [dragPos, setDragPos] = useState(null); // { id, x, y }

  const bodyRef = useRef(null);
  const svgRef = useRef(null);
  const canvasRef = useRef(null);
  const imgSrcRef = useRef(null);
  const panState = useRef({ panning: false, lastX: 0, lastY: 0, moved: false });
  // Intrinsische Seitengrösse: für PDF in Punkten (1 pt = 1/72"), für Bilder in
  // Pixeln. Grundlage der Massberechnung (nur PDF ist massstäblich messbar).
  const intrinsicRef = useRef(null); // { width, height, unit: "pt" | "px" }

  // Hochauflösendes Nachrendern beim Hineinzoomen (gegen Verpixelung): die
  // CSS-Grösse des Canvas bleibt immer die "logische" naturalSize (dafür ist
  // ausschliesslich der Zoom-Transform zuständig); nur die interne
  // Canvas-Auflösung (das Vielfache "oversample") wächst mit dem Zoom, damit
  // pdf.js bei Bedarf schärfer nachrastert.
  const pdfDocRef = useRef(null);
  const fitScaleRef = useRef(1);
  const renderedScaleRef = useRef(1);
  const zoomRenderTimer = useRef(null);

  const numPages = version?.seitenanzahl || 1;

  // Rastert die aktuelle PDF-Seite in der Ziel-Auflösung (Vielfaches der
  // Basisauflösung) neu in den bestehenden Canvas – ohne naturalSize/Zoom/Pan
  // zu verändern, damit Overlay/Markierungen unberührt bleiben.
  const renderPdfAtScale = useCallback(
    async (oversample) => {
      const canvas = canvasRef.current;
      if (!canvas || !pdfDocRef.current) return;
      const pdfPage = await pdfDocRef.current.getPage(page);
      const dpr = window.devicePixelRatio || 1;
      const viewport = pdfPage.getViewport({
        scale: fitScaleRef.current * dpr * oversample,
      });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      await pdfPage.render({ canvasContext: ctx, viewport }).promise;
      renderedScaleRef.current = oversample;
    },
    [page]
  );

  // --- Seite rendern (PDF via pdf.js auf Canvas, Bild direkt als <img>) -----
  const renderPage = useCallback(async () => {
    if (!version?.blobPath || !bodyRef.current) return;
    setLoading(true);
    setError(null);
    // Vorherige intrinsische Grösse verwerfen, damit Messen nicht kurzzeitig mit
    // den Werten des alten Plans/der alten Seite rechnet (bzw. nach Ladefehler).
    intrinsicRef.current = null;
    try {
      const containerWidth = Math.max(200, bodyRef.current.clientWidth - 40);
      const url = buildPlanFileUrl(version.blobPath);

      if (isPdf(version.blobPath)) {
        const doc = await pdfjsLib.getDocument({ url }).promise;
        pdfDocRef.current = doc;
        const pdfPage = await doc.getPage(page);
        const unscaled = pdfPage.getViewport({ scale: 1 });
        const fitScale = containerWidth / unscaled.width;
        fitScaleRef.current = fitScale;

        const canvas = canvasRef.current;
        canvas.style.width = `${fitScale * unscaled.width}px`;
        canvas.style.height = `${fitScale * unscaled.height}px`;
        await renderPdfAtScale(1);

        imgSrcRef.current = null;
        // PDF-Seitengrösse in Punkten (massstäblich messbar).
        intrinsicRef.current = {
          width: unscaled.width,
          height: unscaled.height,
          unit: "pt",
        };
        setNaturalSize({
          width: fitScale * unscaled.width,
          height: fitScale * unscaled.height,
        });
      } else {
        pdfDocRef.current = null;
        const size = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
          img.onerror = () => reject(new Error("Bild konnte nicht geladen werden."));
          img.src = url;
        });
        const fitScale = containerWidth / size.w;
        imgSrcRef.current = url;
        // Bild: nur Pixel bekannt, keine physische Grösse -> nicht massstäblich.
        intrinsicRef.current = { width: size.w, height: size.h, unit: "px" };
        setNaturalSize({ width: fitScale * size.w, height: fitScale * size.h });
      }
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } catch (err) {
      console.error("Plan konnte nicht geladen werden:", err);
      setError("Der Plan konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, page, renderPdfAtScale]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  // Beim Hineinzoomen (entprellt) in höherer Auflösung nachrastern, damit der
  // Plan scharf bleibt statt nur per CSS-Transform hochskaliert zu werden.
  useEffect(() => {
    if (loading || !isPdf(version?.blobPath)) return undefined;
    const target = Math.min(zoom, MAX_RENDER_OVERSAMPLE);
    if (target <= renderedScaleRef.current + 0.01) return undefined;
    if (zoomRenderTimer.current) clearTimeout(zoomRenderTimer.current);
    zoomRenderTimer.current = setTimeout(() => {
      renderPdfAtScale(target).catch((err) =>
        console.error("Plan konnte nicht in höherer Auflösung neu gerendert werden:", err)
      );
    }, 200);
    return () => clearTimeout(zoomRenderTimer.current);
  }, [zoom, loading, version, renderPdfAtScale]);

  // --- Vergleichsversion rendern (US-PLAN-006: Vorher-Nachher-Überblendung) --
  // Wird ohne eigene Ausrichtung auf die Grösse der aktuellen Version gestreckt
  // ("einfacher Vergleich", bewusst keine Pixel-Ausrichtung/KI-Overlay).
  useEffect(() => {
    if (!compareVersionId || !compareVersion || !naturalSize) {
      setCompareImgSrc(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = buildPlanFileUrl(compareVersion.blobPath);
        if (isPdf(compareVersion.blobPath)) {
          const doc = await pdfjsLib.getDocument({ url }).promise;
          const pageNum = Math.min(page, doc.numPages);
          const pdfPage = await doc.getPage(pageNum);
          const unscaled = pdfPage.getViewport({ scale: 1 });
          const scale = naturalSize.width / unscaled.width;
          const dpr = window.devicePixelRatio || 1;
          const viewport = pdfPage.getViewport({ scale: scale * dpr });
          const canvas = compareCanvasRef.current;
          if (!canvas || cancelled) return;
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${naturalSize.width}px`;
          canvas.style.height = `${naturalSize.height}px`;
          const ctx = canvas.getContext("2d");
          await pdfPage.render({ canvasContext: ctx, viewport }).promise;
          if (!cancelled) setCompareImgSrc(null);
        } else if (!cancelled) {
          setCompareImgSrc(url);
        }
      } catch (err) {
        console.error("Vergleichsversion konnte nicht geladen werden:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [compareVersionId, compareVersion, naturalSize, page]);

  // --- Markierungen der aktuellen Seite laden --------------------------------
  const loadMarkups = useCallback(async () => {
    if (!version?.id) return;
    try {
      const data = await getMarkupsForPlanVersion(version.id, page);
      setMarkups(data);
    } catch (err) {
      setError(err.message || "Die Markierungen konnten nicht geladen werden.");
    }
  }, [version, page]);

  useEffect(() => {
    loadMarkups();
    // reloadToken erzwingt ein Neuladen nach externen Änderungen (Plan-Ansicht).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMarkups, reloadToken]);

  // Beim Seiten-/Versionswechsel angefangene, noch nicht abgeschlossene Zeichnungen
  // bzw. Messungen verwerfen: ihre normierten Koordinaten gälten sonst fälschlich
  // auf der neuen Seite (und ein Abschluss würde eine falsch verortete Markierung
  // mit der NEUEN Seite speichern).
  useEffect(() => {
    setDrawingPoints([]);
    setMeasurePoints([]);
  }, [page, versionId]);

  // Pendenzenliste für die Zuordnungsauswahl nur bei Bedarf (nicht readOnly) laden.
  useEffect(() => {
    if (readOnly || !project?.id) return;
    getPendenzen(project.id)
      .then(setPendenzenList)
      .catch(() => setPendenzenList([]));
  }, [readOnly, project]);

  // Auf eine bestimmte Markierung zentrieren (US-PLAN-005: Liste -> Springen).
  useEffect(() => {
    if (!focusMarkupId || !naturalSize || !bodyRef.current) return;
    const m = markups.find((x) => x.id === focusMarkupId);
    if (!m || !m.geometrie) return;
    const point = m.typ === "point" ? m.geometrie : m.geometrie.points?.[0];
    if (!point) return;
    const bodyRect = bodyRef.current.getBoundingClientRect();
    const targetZoom = Math.min(2, MAX_ZOOM);
    const contentX = point.x * naturalSize.width;
    const contentY = point.y * naturalSize.height;
    setZoom(targetZoom);
    setPan({
      x: bodyRect.width / 2 - contentX * targetZoom,
      y: bodyRect.height / 2 - contentY * targetZoom,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMarkupId, naturalSize]);

  // --- Zoom (Mausrad) --------------------------------------------------------
  // Als nativer, nicht-passiver Listener registriert (siehe Effekt weiter
  // unten): React hängt "wheel" standardmässig passiv ein, wodurch
  // e.preventDefault() stillschweigend ignoriert wird und die Seite/das
  // Modal dahinter beim Zoomen mitscrollt.
  const handleWheel = useCallback((e) => {
    if (!bodyRef.current) return;
    e.preventDefault();
    const rect = bodyRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom((prevZoom) => {
      const newZoom = clampZoom(prevZoom * factor);
      setPan((prevPan) => {
        const contentX = (mouseX - prevPan.x) / prevZoom;
        const contentY = (mouseY - prevPan.y) / prevZoom;
        return {
          x: mouseX - contentX * newZoom,
          y: mouseY - contentY * newZoom,
        };
      });
      return newZoom;
    });
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const zoomBy = (factor) => {
    setZoom((z) => clampZoom(z * factor));
  };

  const normalizedFromEvent = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  // --- Punkt-Markierung verschieben (US-PLAN-006) ------------------------------
  // Ohne Bearbeitungsrecht ist nur das Anklicken zum Öffnen erlaubt, kein
  // Verschieben — daher hier direkt behandelt statt über das Drag-Tracking.
  const handlePointMouseDown = (e, m) => {
    // Nur im Auswählen-Modus reagiert die Markierung selbst. Beim Zeichnen/Messen
    // den Klick NICHT abfangen (kein stopPropagation) – er soll als Flächen-Klick
    // an handleSvgClick durchgereicht werden, statt den Punkt zu verschieben oder
    // das Zeichnen/Messen zu unterbrechen.
    if (tool !== "select") return;
    e.stopPropagation();
    if (readOnly) {
      setSelectedMarkupId(m.id);
      onOpenPendenz?.(m.pendenzId);
      return;
    }
    dragRef.current = { id: m.id, pendenzId: m.pendenzId, original: m, moved: false };
    setDragPos({ id: m.id, x: m.geometrie.x, y: m.geometrie.y });
  };

  // --- Pan (Ziehen) / Punkt verschieben ----------------------------------------
  const [isPanning, setIsPanning] = useState(false);

  const handleMouseDown = (e) => {
    if (dragRef.current) return;
    // Mittlere Maustaste pannt immer, unabhängig vom aktiven Werkzeug (auch
    // während des Zeichnens einer Linie/Fläche); linke Maustaste nur im
    // Auswählen-Werkzeug (sonst würde sie stattdessen zeichnen).
    const isMiddleButton = e.button === 1;
    const isLeftSelect = e.button === 0 && tool === "select";
    // WICHTIG: Jeder frische Druck setzt "moved" zurück – auch bei den
    // Zeichenwerkzeugen (die hier gleich per return aussteigen). Sonst bleibt ein
    // zuvor beim Pannen gesetztes moved=true stehen, und handleSvgClick verwirft
    // danach jeden Zeichen-Klick als vermeintlichen Pan-Drag. Das war die Ursache
    // dafür, dass nach Zoomen/Pannen bzw. nach einem Plan-Wechsel keine
    // Markierungen (v.a. Flächen) mehr gesetzt werden konnten.
    panState.current.moved = false;
    if (!isMiddleButton && !isLeftSelect) return;
    if (isMiddleButton) e.preventDefault();
    panState.current = {
      panning: true,
      lastX: e.clientX,
      lastY: e.clientY,
      moved: false,
    };
    setIsPanning(true);
  };

  const handleMouseMove = (e) => {
    if (dragRef.current) {
      const pos = normalizedFromEvent(e);
      dragRef.current.moved = true;
      setDragPos({ id: dragRef.current.id, x: pos.x, y: pos.y });
      return;
    }
    // Vorschau: sobald beim Zeichnen/Messen ein Punkt gesetzt ist, folgt das
    // nächste Segment der Maus. Nur dann tracken (sonst keine Extra-Renders).
    const drawingLA =
      (tool === "line" || tool === "area" || tool === "arrow") &&
      drawingPoints.length > 0;
    const measuringLA =
      (tool === "measure-dist" || tool === "measure-area") &&
      measurePoints.length > 0;
    if (drawingLA || measuringLA) {
      setHoverPoint(normalizedFromEvent(e));
    }
    if (!panState.current.panning) return;
    const dx = e.clientX - panState.current.lastX;
    const dy = e.clientY - panState.current.lastY;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) panState.current.moved = true;
    panState.current.lastX = e.clientX;
    panState.current.lastY = e.clientY;
    setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
  };

  const handleMouseUp = async () => {
    if (dragRef.current) {
      const { id, pendenzId, original, moved } = dragRef.current;
      dragRef.current = null;
      if (moved && dragPos) {
        try {
          await updateMarkup(pendenzId, id, {
            ...original,
            geometrie: { x: dragPos.x, y: dragPos.y },
            positionPruefen: false,
          });
          await loadMarkups();
          onDataChanged?.();
        } catch (err) {
          setError(err.message || "Die Markierung konnte nicht verschoben werden.");
        }
      } else {
        // Reiner Klick ohne Verschieben: Markierung auswählen (Hervorhebung/
        // Rückrichtung Liste) und – falls verdrahtet (Plan-Ansicht) – die
        // zugehörige Pendenz direkt öffnen.
        setSelectedMarkupId(id);
        onOpenPendenz?.(pendenzId);
      }
      setDragPos(null);
      return;
    }
    panState.current.panning = false;
    setIsPanning(false);
  };

  // --- Zeichnen (Punkt/Linie/Fläche) ------------------------------------------
  const finishDraw = useCallback(
    (points, typ) => {
      let geometrie = null;
      if (typ === "point") {
        geometrie = points[0];
      } else if (typ === "line" && points.length >= 2) {
        geometrie = { points };
      } else if (typ === "arrow" && points.length >= 2) {
        // Pfeil = gerichtete Strecke von Start zu Ende (genau zwei Punkte).
        geometrie = { points: [points[0], points[points.length - 1]] };
      } else if (typ === "area" && points.length >= 3) {
        geometrie = { points };
      }
      if (geometrie) {
        if (composeMode) {
          onDraftChange?.({ typ, geometrie, planVersionId: version?.id, seite: page });
        } else if (onRequestAssign && !fixedPendenzId) {
          // Plan-Ansicht: Zuordnung/Neuerfassung übernimmt ein volles Modal im
          // Elternteil (alle Pendenz-Felder), nicht das Mini-Popup hier.
          onRequestAssign({ typ, geometrie, planVersionId: version?.id, seite: page });
        } else {
          setAssignDraft({ typ, geometrie });
        }
      }
      setDrawingPoints([]);
      setTool("select");
    },
    [composeMode, onDraftChange, onRequestAssign, fixedPendenzId, version, page]
  );

  const handleSvgClick = (e) => {
    if (e.button !== 0) return; // nur linke Maustaste
    if (panState.current.moved) return; // war ein Pan-Drag, kein Klick
    if (tool === "select") {
      // Klick ins Leere hebt die Auswahl auf. Klicks direkt auf eine Markierung
      // stoppen die Propagierung (onClick/onMouseDown der Form), erreichen dieses
      // Handler also nicht als Ziel === SVG. Gilt auch ohne Bearbeitungsrecht,
      // da die Auswahl rein visuell ist.
      if (e.target === e.currentTarget) setSelectedMarkupId(null);
      return;
    }
    const point = normalizedFromEvent(e);
    // Messen ist auch ohne Bearbeitungsrecht erlaubt (rein flüchtig, keine Markierung).
    if (tool === "measure-dist" || tool === "measure-area") {
      setMeasurePoints((prev) => [...prev, point]);
      setHoverPoint(point); // Vorschau am Cursor starten (kein Sprung zur Altposition)
      return;
    }
    if (readOnly) return;
    if (tool === "point") {
      finishDraw([point], "point");
    } else if (tool === "arrow") {
      // Zwei Klicks: Start, dann Ende – danach ist der Pfeil fertig.
      const next = [...drawingPoints, point];
      if (next.length >= 2) finishDraw(next, "arrow");
      else {
        setDrawingPoints(next);
        setHoverPoint(point);
      }
    } else {
      setDrawingPoints((prev) => [...prev, point]);
      setHoverPoint(point);
    }
  };

  const handleSvgDoubleClick = () => {
    if (tool === "line" || tool === "area") {
      finishDraw(drawingPoints, tool);
    }
  };

  // Rechtsklick beim Zeichnen/Messen: Linie/Fläche abschliessen (wie Doppelklick)
  // bzw. Messung beenden – statt das Browser-Kontextmenü zu öffnen. In den übrigen
  // Werkzeugen (Auswählen/Punkt/Pfeil) bleibt das normale Kontextmenü erhalten.
  const handleSvgContextMenu = (e) => {
    const drawing = tool === "line" || tool === "area";
    const measuring = tool === "measure-dist" || tool === "measure-area";
    if (!drawing && !measuring) return;
    e.preventDefault();
    if (drawing && drawingPoints.length > 0) {
      finishDraw(drawingPoints, tool);
    }
    // Messen hat keinen eigenen Abschluss (Ergebnis bleibt live sichtbar); der
    // Rechtsklick unterdrückt hier nur das Kontextmenü.
  };

  // Ein Zeichen-Werkzeug (Punkt/Linie/Fläche) aktivieren: hebt zuerst eine
  // bestehende Auswahl auf (die zuvor angewählte Pendenz wird "deaktiviert"),
  // danach wird gezeichnet und beim Abschluss eine neue Pendenz erfasst.
  const activateDrawTool = (nextTool) => {
    setSelectedMarkupId(null);
    setDrawingPoints([]);
    setMeasurePoints([]);
    setTool(nextTool);
  };

  // Mess-Werkzeug aktivieren (Toggle: erneuter Klick schaltet zurück auf
  // "Auswählen"). Messen ist auch ohne Bearbeitungsrecht möglich.
  const activateMeasureTool = (nextTool) => {
    setSelectedMarkupId(null);
    setDrawingPoints([]);
    setMeasurePoints([]);
    setTool((prev) => (prev === nextTool ? "select" : nextTool));
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (assignDraft) setAssignDraft(null);
        else if (drawingPoints.length) setDrawingPoints([]);
        else if (measurePoints.length) setMeasurePoints([]);
        else if (tool !== "select") setTool("select");
        else if (!compact) onClose?.();
      }
      if (e.key === "Enter" && (tool === "line" || tool === "area")) {
        finishDraw(drawingPoints, tool);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [assignDraft, drawingPoints, measurePoints, tool, compact, onClose, finishDraw]);

  // --- Zuordnung speichern (bestehende oder neue Pendenz) ---------------------
  const handleSaveAssign = async ({ pendenzId, newPendenz, beschriftung }) => {
    try {
      setError(null);
      let targetId = pendenzId;
      if (newPendenz) {
        const created = await createPendenz(project.id, {
          typ: newPendenz.typ,
          kategorie: "Div",
          beschreibung: newPendenz.beschreibung,
          verantwortlichIds: [],
          verantwortlich: "",
          terminSoll: "",
          terminNeu: "",
          status: "offen",
          kommentar: "",
          checkliste: [],
          sichtbarExtern: true,
          erstelltVon: user?.email || user?.userDetails || "",
        });
        targetId = created.id;
      }
      await createMarkup(targetId, {
        projectUuid: project.id,
        planId: plan.id,
        planVersionId: version.id,
        seite: page,
        typ: assignDraft.typ,
        geometrie: assignDraft.geometrie,
        beschriftung: beschriftung || "",
        erstelltVon: user?.email || user?.userDetails || "",
      });
      setAssignDraft(null);
      await loadMarkups();
      onDataChanged?.();
      // Im Direkteinstieg (feste Pendenz) bleibt das Punkt-Werkzeug aktiv, damit
      // mehrere Markierungen hintereinander gesetzt werden können.
      if (fixedPendenzId) setTool("point");
    } catch (err) {
      setError(err.message || "Die Markierung konnte nicht gespeichert werden.");
    }
  };

  const handleDeleteMarkup = async (markup) => {
    if (!window.confirm("Diese Markierung wirklich löschen?")) return;
    try {
      await deleteMarkup(markup.pendenzId, markup.id);
      setSelectedMarkupId(null);
      await loadMarkups();
      onDataChanged?.();
    } catch (err) {
      setError(err.message || "Die Markierung konnte nicht gelöscht werden.");
    }
  };

  // US-PLAN-006: nach Fortschreiben auf eine neue Planversion bestätigt die
  // Position ohne Verschieben (positionPruefen -> false).
  const handleConfirmPosition = async (markup) => {
    try {
      await updateMarkup(markup.pendenzId, markup.id, {
        ...markup,
        positionPruefen: false,
      });
      await loadMarkups();
      onDataChanged?.();
    } catch (err) {
      setError(err.message || "Die Position konnte nicht bestätigt werden.");
    }
  };

  const selectedMarkup = markups.find((m) => m.id === selectedMarkupId) || null;
  const selectedPendenz = selectedMarkup
    ? pendenzenList.find((p) => p.id === selectedMarkup.pendenzId)
    : null;

  // Suche/Filter der Plan-Ansicht blenden auch Markierungen aus (nur die der
  // übergebenen, aktuell sichtbaren Pendenzen werden gezeichnet).
  const visiblePendenzIdSet = useMemo(
    () => (visiblePendenzIds ? new Set(visiblePendenzIds) : null),
    [visiblePendenzIds]
  );

  // --- Messen (nur PDF mit erfasstem Massstab) --------------------------------
  const massstab = plan?.massstab || null; // Nenner, z.B. 100 für 1:100
  const intrinsic = intrinsicRef.current;
  const canMeasure = !!(intrinsic && intrinsic.unit === "pt" && massstab);
  // 1 PDF-Punkt = 25.4/72 mm auf dem Papier; real = × Massstab -> Meter pro Punkt.
  const metersPerPoint = canMeasure ? (25.4 / 72 / 1000) * massstab : 0;

  const measureDistanceM = (pts) => {
    if (!canMeasure || !pts || pts.length < 2) return null;
    const { width: iw, height: ih } = intrinsic;
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
      const dx = (pts[i].x - pts[i - 1].x) * iw;
      const dy = (pts[i].y - pts[i - 1].y) * ih;
      total += Math.hypot(dx, dy);
    }
    return total * metersPerPoint;
  };

  const measureAreaM2 = (pts) => {
    if (!canMeasure || !pts || pts.length < 3) return null;
    const { width: iw, height: ih } = intrinsic;
    let sum = 0;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      sum += a.x * iw * (b.y * ih) - b.x * iw * (a.y * ih);
    }
    return (Math.abs(sum) / 2) * metersPerPoint * metersPerPoint;
  };

  const fmtDist = (m) =>
    m == null ? "" : m < 1 ? `${(m * 100).toFixed(1)} cm` : `${m.toFixed(2)} m`;
  const fmtArea = (a) => (a == null ? "" : `${a.toFixed(2)} m²`);

  // Messpunkte inkl. Cursor-Vorschau (Live-Wert folgt der Maus).
  const isMeasuring = tool === "measure-dist" || tool === "measure-area";
  const measurePreview =
    isMeasuring && hoverPoint ? [...measurePoints, hoverPoint] : measurePoints;

  const renderMarkupShape = (m) => {
    if (!m.geometrie || !naturalSize) return null;
    if (visiblePendenzIdSet && !visiblePendenzIdSet.has(m.pendenzId)) return null;
    const highlighted =
      highlightMarkupIds.includes(m.id) || selectedMarkupId === m.id;
    const onClick = (e) => {
      // Beim Zeichnen/Messen nicht abfangen – Klick an die Fläche durchreichen.
      if (tool !== "select") return;
      e.stopPropagation();
      setSelectedMarkupId(m.id);
      onOpenPendenz?.(m.pendenzId);
    };
    if (m.typ === "point") {
      const livePos = dragPos && dragPos.id === m.id ? dragPos : m.geometrie;
      const cx = px(livePos.x, naturalSize.width);
      const cy = px(livePos.y, naturalSize.height);
      return (
        <g key={m.id}>
          <circle
            cx={cx}
            cy={cy}
            r={7 / zoom}
            onMouseDown={(e) => handlePointMouseDown(e, m)}
            style={{ cursor: readOnly ? "pointer" : "move" }}
            className={`plan-viewer__markup-point${
              m.positionPruefen ? " plan-viewer__markup-point--check" : ""
            }${highlighted ? " plan-viewer__markup-highlight" : ""}`}
          />
          {m.beschriftung && (
            <text x={cx + 10 / zoom} y={cy + 4 / zoom} className="plan-viewer__markup-label">
              {m.beschriftung}
            </text>
          )}
        </g>
      );
    }
    const pointsAttr = (m.geometrie.points || [])
      .map((p) => `${px(p.x, naturalSize.width)},${px(p.y, naturalSize.height)}`)
      .join(" ");
    const checkStyle = m.positionPruefen ? { strokeDasharray: `${6 / zoom} ${4 / zoom}` } : undefined;
    if (m.typ === "arrow") {
      return (
        <polyline
          key={m.id}
          style={{ cursor: "pointer", ...checkStyle }}
          points={pointsAttr}
          onClick={onClick}
          markerEnd="url(#plan-arrowhead)"
          className={`plan-viewer__markup-arrow${
            highlighted ? " plan-viewer__markup-highlight" : ""
          }`}
        />
      );
    }
    if (m.typ === "line") {
      return (
        <polyline
          key={m.id}
          style={{ cursor: "pointer", ...checkStyle }}
          points={pointsAttr}
          onClick={onClick}
          className={`plan-viewer__markup-line${
            highlighted ? " plan-viewer__markup-highlight" : ""
          }`}
        />
      );
    }
    return (
      <polygon
        key={m.id}
        points={pointsAttr}
        onClick={onClick}
        style={{ cursor: "pointer", ...checkStyle }}
        className={`plan-viewer__markup-area${
          highlighted ? " plan-viewer__markup-highlight" : ""
        }`}
      />
    );
  };

  const renderDraftShape = () => {
    if (!naturalSize) return null;
    const toXY = (p) => `${px(p.x, naturalSize.width)},${px(p.y, naturalSize.height)}`;
    // Gesetzte Vertices als kleine Punkte (Präzisions-Feedback).
    const vertexDots = (pts) =>
      pts.map((p, i) => (
        <circle
          key={i}
          cx={px(p.x, naturalSize.width)}
          cy={px(p.y, naturalSize.height)}
          r={3 / zoom}
          className="plan-viewer__draft-dot"
        />
      ));

    if (tool === "line" || tool === "area") {
      if (drawingPoints.length === 0) return null;
      // Vorschau: das noch offene Segment folgt der Maus (hoverPoint).
      const preview = hoverPoint ? [...drawingPoints, hoverPoint] : drawingPoints;
      const pointsAttr = preview.map(toXY).join(" ");
      return (
        <g>
          {tool === "line" ? (
            <polyline points={pointsAttr} className="plan-viewer__markup-line" />
          ) : (
            <polygon points={pointsAttr} className="plan-viewer__markup-area" />
          )}
          {vertexDots(drawingPoints)}
        </g>
      );
    }
    // Pfeil: nach dem ersten Klick folgt die Pfeilspitze der Maus, bis der zweite
    // Klick abschliesst.
    if (tool === "arrow" && drawingPoints.length > 0) {
      const end = hoverPoint || drawingPoints[0];
      const pointsAttr = [drawingPoints[0], end].map(toXY).join(" ");
      return (
        <g>
          <polyline
            points={pointsAttr}
            markerEnd="url(#plan-arrowhead)"
            className="plan-viewer__markup-arrow"
          />
          {vertexDots([drawingPoints[0]])}
        </g>
      );
    }
    return null;
  };

  // Bereits fertig gezeichnete, aber noch nicht gespeicherte Markierung
  // (Kombi-Erfassung "Neue Planpendenz"): bleibt sichtbar, bis die Pendenz
  // gespeichert oder die Markierung verworfen wird.
  const renderPendingDraft = () => {
    if (!composeMode || !pendingDraft || !naturalSize) return null;
    if (pendingDraft.planVersionId !== version?.id || pendingDraft.seite !== page) return null;
    const { typ, geometrie } = pendingDraft;
    if (typ === "point") {
      const cx = px(geometrie.x, naturalSize.width);
      const cy = px(geometrie.y, naturalSize.height);
      return (
        <circle
          cx={cx}
          cy={cy}
          r={7 / zoom}
          className="plan-viewer__markup-point plan-viewer__markup-draft"
        />
      );
    }
    const pointsAttr = (geometrie.points || [])
      .map((p) => `${px(p.x, naturalSize.width)},${px(p.y, naturalSize.height)}`)
      .join(" ");
    return typ === "line" ? (
      <polyline points={pointsAttr} className="plan-viewer__markup-line plan-viewer__markup-draft" />
    ) : (
      <polygon points={pointsAttr} className="plan-viewer__markup-area plan-viewer__markup-draft" />
    );
  };

  // Flüchtige Messung (Distanz-Linie bzw. Flächen-Polygon) mit Wertlabel.
  const renderMeasure = () => {
    if (!naturalSize || measurePoints.length === 0) return null;
    if (tool !== "measure-dist" && tool !== "measure-area") return null;
    const isArea = tool === "measure-area";
    const pointsAttr = measurePreview
      .map((p) => `${px(p.x, naturalSize.width)},${px(p.y, naturalSize.height)}`)
      .join(" ");
    const last = measurePreview[measurePreview.length - 1];
    const val = isArea
      ? fmtArea(measureAreaM2(measurePreview))
      : fmtDist(measureDistanceM(measurePreview));
    return (
      <g>
        {isArea ? (
          <polygon
            points={pointsAttr}
            className="plan-viewer__measure-shape plan-viewer__measure-shape--area"
          />
        ) : (
          <polyline points={pointsAttr} className="plan-viewer__measure-shape" />
        )}
        {measurePoints.map((p, i) => (
          <circle
            key={i}
            cx={px(p.x, naturalSize.width)}
            cy={px(p.y, naturalSize.height)}
            r={3 / zoom}
            className="plan-viewer__measure-dot"
          />
        ))}
        {canMeasure && val && (
          <text
            x={px(last.x, naturalSize.width) + 8 / zoom}
            y={px(last.y, naturalSize.height) - 8 / zoom}
            className="plan-viewer__measure-label"
          >
            {val}
          </text>
        )}
      </g>
    );
  };

  const containerClass = `plan-viewer${compact ? " plan-viewer--compact" : ""}`;

  const content = (
    <div
      className="plan-viewer__panel"
      style={compactHeight ? { height: compactHeight } : undefined}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="plan-viewer__header">
        <div>
          <h4>{plan?.name}</h4>
          <span className="plan-viewer__meta">
            {plan?.geschoss ? `${plan.geschoss} · ` : ""}
            Rev. {version?.revision || "–"}
            {version?.ausgabedatum ? ` vom ${version.ausgabedatum}` : ""}
            {version?.superseded ? " (ersetzt)" : ""}
          </span>
        </div>
        {!compact && versions.length > 1 && (
          <div className="plan-viewer__toolbar">
            <select value={versionId || ""} onChange={(e) => handleVersionChange(e.target.value)}>
              {[...versions].reverse().map((v) => (
                <option key={v.id} value={v.id}>
                  Rev. {v.revision}{v.superseded ? " (ersetzt)" : ""}
                </option>
              ))}
            </select>
            <select
              value={compareVersionId}
              onChange={(e) => setCompareVersionId(e.target.value)}
              title="Vorher-Nachher-Vergleich"
            >
              <option value="">Kein Vergleich</option>
              {versions
                .filter((v) => v.id !== versionId)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    Vergleiche mit Rev. {v.revision}
                  </option>
                ))}
            </select>
            {compareVersionId && (
              <input
                type="range"
                min={0}
                max={100}
                value={blendOpacity}
                onChange={(e) => setBlendOpacity(Number(e.target.value))}
                title="Überblendung"
              />
            )}
          </div>
        )}
        {!readOnly && (
          <div className="plan-viewer__toolbar">
            <button
              type="button"
              className={`plan-viewer__tool-btn${tool === "select" ? " is-active" : ""}`}
              onClick={() => {
                setTool("select");
                setDrawingPoints([]);
              }}
            >
              Auswählen
            </button>
            <button
              type="button"
              className={`plan-viewer__tool-btn${tool === "point" ? " is-active" : ""}`}
              onClick={() => activateDrawTool("point")}
            >
              Punkt
            </button>
            <button
              type="button"
              className={`plan-viewer__tool-btn${tool === "line" ? " is-active" : ""}`}
              onClick={() => activateDrawTool("line")}
            >
              Linie
            </button>
            <button
              type="button"
              className={`plan-viewer__tool-btn${tool === "arrow" ? " is-active" : ""}`}
              onClick={() => activateDrawTool("arrow")}
            >
              Pfeil
            </button>
            <button
              type="button"
              className={`plan-viewer__tool-btn${tool === "area" ? " is-active" : ""}`}
              onClick={() => activateDrawTool("area")}
            >
              Fläche
            </button>
          </div>
        )}
        <div className="plan-viewer__toolbar">
          <button
            type="button"
            className={`plan-viewer__tool-btn${tool === "measure-dist" ? " is-active" : ""}`}
            onClick={() => activateMeasureTool("measure-dist")}
            title="Distanz messen"
          >
            Distanz messen
          </button>
          <button
            type="button"
            className={`plan-viewer__tool-btn${tool === "measure-area" ? " is-active" : ""}`}
            onClick={() => activateMeasureTool("measure-area")}
            title="Fläche messen"
          >
            Fläche messen
          </button>
        </div>
        <div className="plan-viewer__toolbar">
          <button type="button" className="plan-viewer__tool-btn" onClick={() => zoomBy(1.25)}>
            +
          </button>
          <button type="button" className="plan-viewer__tool-btn" onClick={() => zoomBy(1 / 1.25)}>
            –
          </button>
          {!compact && (
            <button type="button" className="plan-viewer__close" onClick={onClose} aria-label="Schliessen">
              ×
            </button>
          )}
        </div>
      </div>

      {error && <div className="plan-viewer__error">{error}</div>}

      {fixedPendenzId && (
        <div className="plan-viewer__pin-hint">
          Verorten für <strong>{fixedPendenzLabel || "diese Pendenz"}</strong> — Werkzeug
          wählen und auf den Plan klicken.
        </div>
      )}

      {(tool === "line" || tool === "area") && drawingPoints.length > 0 && (
        <div className="plan-viewer__draw-hint">
          <span>
            {drawingPoints.length} Punkt{drawingPoints.length === 1 ? "" : "e"} gesetzt ·
            Klicken für weitere{tool === "area" ? " (mind. 3)" : ""}, dann abschliessen
          </span>
          <button
            type="button"
            className="plan-viewer__tool-btn"
            onClick={() => finishDraw(drawingPoints, tool)}
          >
            Abschliessen
          </button>
          <button
            type="button"
            className="plan-viewer__tool-btn"
            onClick={() => setDrawingPoints([])}
          >
            Verwerfen
          </button>
        </div>
      )}

      {(tool === "measure-dist" || tool === "measure-area") && (
        <div className="plan-viewer__draw-hint">
          {!canMeasure ? (
            <span>
              {intrinsicRef.current && intrinsicRef.current.unit !== "pt"
                ? "Messen ist nur bei PDF-Plänen möglich (Bilder haben keine physische Grösse)."
                : "Bitte zuerst den Massstab dieses Plans erfassen (Tab „Pläne“, „Bearbeiten“)."}
            </span>
          ) : tool === "measure-dist" ? (
            <span>
              {measurePreview.length >= 2
                ? `Distanz: ${fmtDist(measureDistanceM(measurePreview))}`
                : "Punkte klicken – die Strecke wird summiert."}
            </span>
          ) : (
            <span>
              {measurePreview.length >= 3
                ? `Fläche: ${fmtArea(measureAreaM2(measurePreview))} · Umfang: ${fmtDist(
                    measureDistanceM([...measurePreview, measurePreview[0]])
                  )}`
                : "Mindestens 3 Punkte klicken (Fläche)."}
            </span>
          )}
          <button
            type="button"
            className="plan-viewer__tool-btn"
            onClick={() => setMeasurePoints([])}
          >
            Zurücksetzen
          </button>
        </div>
      )}

      <div
        ref={bodyRef}
        className={`plan-viewer__body${tool !== "select" ? " is-drawing" : ""}${
          isPanning ? " is-panning" : ""
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          handleMouseUp();
          setHoverPoint(null);
        }}
      >
        {loading && <div className="plan-viewer__loading">Plan wird geladen...</div>}
        <div
          className="plan-viewer__stage"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            width: naturalSize ? naturalSize.width : 0,
            height: naturalSize ? naturalSize.height : 0,
          }}
        >
          {isPdf(version?.blobPath) ? (
            // Muss unabhängig von naturalSize gerendert werden: renderPage()
            // braucht canvasRef.current bereits, BEVOR naturalSize gesetzt wird
            // (Grösse wird erst nach dem pdf.js-Rendering bekannt).
            <canvas ref={canvasRef} className="plan-viewer__canvas" />
          ) : (
            naturalSize && (
              <img
                src={imgSrcRef.current}
                alt={plan?.name}
                className="plan-viewer__image"
                style={{ width: naturalSize.width, height: naturalSize.height }}
                draggable={false}
              />
            )
          )}
          {naturalSize && (
            <>
              {compareVersionId && compareVersion && (
                isPdf(compareVersion.blobPath) ? (
                  <canvas
                    ref={compareCanvasRef}
                    className="plan-viewer__canvas plan-viewer__compare-layer"
                    style={{ opacity: blendOpacity / 100 }}
                  />
                ) : (
                  <img
                    src={compareImgSrc}
                    alt="Vergleichsversion"
                    className="plan-viewer__image plan-viewer__compare-layer"
                    style={{
                      width: naturalSize.width,
                      height: naturalSize.height,
                      opacity: blendOpacity / 100,
                    }}
                    draggable={false}
                  />
                )
              )}
              <svg
                ref={svgRef}
                className="plan-viewer__overlay plan-viewer__overlay-interactive"
                width={naturalSize.width}
                height={naturalSize.height}
                viewBox={`0 0 ${naturalSize.width} ${naturalSize.height}`}
                onClick={handleSvgClick}
                onDoubleClick={handleSvgDoubleClick}
                onContextMenu={handleSvgContextMenu}
              >
                <defs>
                  <marker
                    id="plan-arrowhead"
                    markerWidth="8"
                    markerHeight="8"
                    refX="6"
                    refY="4"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L8,4 L0,8 z" className="plan-viewer__arrowhead" />
                  </marker>
                </defs>
                {markups.map(renderMarkupShape)}
                {renderDraftShape()}
                {renderPendingDraft()}
                {renderMeasure()}
              </svg>
            </>
          )}
        </div>
      </div>

      {selectedMarkup && !readOnly && (
        <div className="plan-viewer__footer">
          <span>
            {selectedMarkup.positionPruefen && (
              <strong className="plan-viewer__check-hint">Position prüfen · </strong>
            )}
            {selectedMarkup.beschriftung || "Markierung"}
            {selectedPendenz
              ? ` · Pendenz Nr. ${selectedPendenz.nummer}: ${richTextToPlainText(selectedPendenz.beschreibung)}`
              : ""}
            {selectedMarkup.typ === "point" && " (zum Verschieben ziehen)"}
          </span>
          <div className="plan-viewer__toolbar">
            {selectedMarkup.positionPruefen && (
              <button
                type="button"
                className="plan-viewer__tool-btn"
                onClick={() => handleConfirmPosition(selectedMarkup)}
              >
                Position bestätigen
              </button>
            )}
            {/* Wenn onOpenPendenz verdrahtet ist (z.B. Plan-Ansicht), öffnet bereits
                der Klick auf die Markierung direkt das Bearbeiten-Modal – dieser
                Button wäre dort nur eine verwirrende Neu-Navigation zur selben Seite. */}
            {!onOpenPendenz && selectedPendenz && project?.code && (
              <button
                type="button"
                className="plan-viewer__tool-btn"
                onClick={() =>
                  navigate(`/main-pendenzen/projekte/${encodeURIComponent(project.code)}`)
                }
              >
                Pendenz öffnen
              </button>
            )}
            <button
              type="button"
              className="plan-viewer__tool-btn"
              onClick={() => handleDeleteMarkup(selectedMarkup)}
            >
              Löschen
            </button>
          </div>
        </div>
      )}

      {numPages > 1 && (
        <div className="plan-viewer__footer">
          <div className="plan-viewer__pages">
            <button
              type="button"
              className="plan-viewer__tool-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Seite
            </button>
            <span>
              Seite {page} / {numPages}
            </span>
            <button
              type="button"
              className="plan-viewer__tool-btn"
              disabled={page >= numPages}
              onClick={() => setPage((p) => Math.min(numPages, p + 1))}
            >
              Seite →
            </button>
          </div>
        </div>
      )}

      {assignDraft && (
        <AssignPopup
          pendenzenList={pendenzenList}
          fixedPendenzId={fixedPendenzId}
          fixedPendenzLabel={fixedPendenzLabel}
          defaultMode={defaultAssignMode}
          onCancel={() => setAssignDraft(null)}
          onSave={handleSaveAssign}
        />
      )}
    </div>
  );

  if (compact) {
    return <div className={containerClass}>{content}</div>;
  }

  return (
    <div className={containerClass} onClick={onClose}>
      {content}
    </div>
  );
};

// --- Zuordnungs-Popup: Markierung einer bestehenden oder neuen Pendenz zuordnen --
// Im Direkteinstieg aus der Pendenzenliste (fixedPendenzId gesetzt) entfällt die
// Auswahl komplett — es wird nur noch die Kurzbeschriftung erfasst.
const AssignPopup = ({
  pendenzenList,
  fixedPendenzId,
  fixedPendenzLabel,
  defaultMode = "existing",
  onCancel,
  onSave,
}) => {
  const [mode, setMode] = useState(defaultMode);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [newTyp, setNewTyp] = useState("P");
  const [newBeschreibung, setNewBeschreibung] = useState("");
  const [beschriftung, setBeschriftung] = useState("");

  const filtered = pendenzenList.filter((p) =>
    search.trim()
      ? richTextToPlainText(p.beschreibung).toLowerCase().includes(search.trim().toLowerCase())
      : true
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (fixedPendenzId) {
      onSave({ pendenzId: fixedPendenzId, beschriftung });
    } else if (mode === "existing") {
      if (!selectedId) return;
      onSave({ pendenzId: selectedId, beschriftung });
    } else {
      if (!newBeschreibung.trim()) return;
      onSave({
        newPendenz: { typ: newTyp, beschreibung: newBeschreibung.trim() },
        beschriftung,
      });
    }
  };

  return (
    <div className="plan-viewer__assign-overlay" onClick={onCancel}>
      <form
        className="plan-viewer__assign-popup"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h4>{fixedPendenzId ? "Markierung speichern" : "Markierung zuordnen"}</h4>

        {fixedPendenzId && (
          <p className="plan-viewer__assign-fixed-hint">
            Wird verortet für: <strong>{fixedPendenzLabel || "diese Pendenz"}</strong>
          </p>
        )}

        {!fixedPendenzId && (
        <div className="plan-viewer__assign-tabs">
          <button
            type="button"
            className={mode === "existing" ? "is-active" : ""}
            onClick={() => setMode("existing")}
          >
            Bestehende Pendenz
          </button>
          <button
            type="button"
            className={mode === "new" ? "is-active" : ""}
            onClick={() => setMode("new")}
          >
            Neue Pendenz
          </button>
        </div>
        )}

        {!fixedPendenzId && (mode === "existing" ? (
          <>
            <input
              type="text"
              placeholder="Suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              size={6}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {filtered.map((p) => (
                <option key={p.id} value={p.id}>
                  Nr. {p.nummer} – {richTextToPlainText(p.beschreibung)}
                </option>
              ))}
            </select>
          </>
        ) : (
          <>
            <label>
              <span>Art</span>
              <select value={newTyp} onChange={(e) => setNewTyp(e.target.value)}>
                <option value="P">Pendenz</option>
                <option value="E">Entscheid</option>
                <option value="N">Notiz</option>
              </select>
            </label>
            <label>
              <span>Beschreibung</span>
              <textarea
                value={newBeschreibung}
                onChange={(e) => setNewBeschreibung(e.target.value)}
                rows={2}
              />
            </label>
          </>
        ))}

        <label>
          <span>Kurzbeschriftung (optional)</span>
          <input
            type="text"
            value={beschriftung}
            onChange={(e) => setBeschriftung(e.target.value)}
            placeholder="z.B. Zufahrt Einstellhalle"
          />
        </label>

        <div className="plan-viewer__assign-actions">
          <button type="button" onClick={onCancel}>
            Abbrechen
          </button>
          <button type="submit" className="is-primary">
            Speichern
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlanViewer;
